/**
 * @quantabit/sdk-config - BaseApiClient
 * 
 * SDK API 客户端基类
 * 封装所有通用的配置、请求、Token 管理逻辑
 * 
 * 使用方式：
 * ```javascript
 * import { BaseApiClient } from '@quantabit/sdk-config';
 * 
 * class MyApiClient extends BaseApiClient {
 *   constructor(config = {}) {
 *     super('/my-module', config);  // 只需传入模块路径
 *   }
 *   
 *   // 专注于业务方法
 *   async getList() {
 *     return this.request('/list');
 *   }
 * }
 * ```
 */

import {
  getConfig,
  subscribeConfig,
  getToken as getStoredToken,
  logger,
} from './index.js';

function joinUrlSegments(baseUrl = '', path = '') {
  const cleanBase = String(baseUrl || '').replace(/\/+$/, '');
  const cleanPath = String(path || '').replace(/^\/+/, '');

  if (!cleanBase) {
    return cleanPath ? `/${cleanPath}` : '';
  }

  if (!cleanPath) {
    return cleanBase;
  }

  return `${cleanBase}/${cleanPath}`;
}

async function parseResponseBody(response) {
  if (response.status === 204 || response.status === 205) {
    return null;
  }

  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    return response.json();
  }

  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * SDK API 客户端基类
 * 所有 SDK 的 API 客户端都应继承此类
 */
export class BaseApiClient {
  /**
   * 构造函数
   * @param {string} modulePath - 模块路径，如 '/auth', '/points', '/wallet'
   * @param {Object} config - 配置选项
   * @param {string} config.baseUrl - 自定义基础 URL（覆盖全局配置）
   * @param {number} config.timeout - 自定义超时时间（毫秒）
   * @param {number} config.maxRetries - 最大重试次数（默认 0 不重试）
   * @param {number} config.retryDelay - 重试间隔基数毫秒（默认 1000，指数退避）
   * @param {Function} config.onError - 错误回调
   * @param {Function} config.getLanguage - 获取语言的函数
   */
  constructor(modulePath = '', config = {}) {
    this.modulePath = modulePath;
    this._customBaseUrl = config.baseUrl;
    this._customTimeout = config.timeout;
    this.maxRetries = config.maxRetries || 0;
    this.retryDelay = config.retryDelay || 1000;
    this.token = null;
    this.onError = config.onError;
    this.getLanguage = config.getLanguage || (() => 'zh');

    // 从全局配置初始化
    this._updateFromGlobalConfig(getConfig());

    // 订阅配置变更（使用 WeakRef 避免内存泄漏）
    if (typeof WeakRef !== 'undefined') {
      const weakSelf = new WeakRef(this);
      let unsubscribe;
      const listener = (newConfig) => {
        const self = weakSelf.deref();
        if (self) {
          self._updateFromGlobalConfig(newConfig);
        } else if (unsubscribe) {
          unsubscribe();
        }
      };
      unsubscribe = subscribeConfig(listener);
      this._unsubscribe = unsubscribe;
    } else {
      this._unsubscribe = subscribeConfig((newConfig) => {
        this._updateFromGlobalConfig(newConfig);
      });
    }
  }

  /**
   * 从全局配置更新本地配置
   * @private
   */
  _updateFromGlobalConfig(globalConfig) {
    // 只有未自定义时才使用全局配置
    if (!this._customBaseUrl) {
      let base = globalConfig.apiBaseUrl || '/api/v1';
      const isAbsolute = /^https?:\/\//i.test(base);
      const isServer = typeof window === 'undefined';
      if (!isAbsolute && isServer) {
        // 如果是 Server 端或 Node.js 环境，将相对路径补全为完整品牌域名路径
        const fullUrl = globalConfig.apiFullUrl || 'https://api.qbitchain.io';
        base = joinUrlSegments(fullUrl, base);
      }
      this.baseUrl = joinUrlSegments(base, this.modulePath);
    } else {
      this.baseUrl = joinUrlSegments(this._customBaseUrl, '');
    }

    if (!this._customTimeout) {
      this.timeout = globalConfig.timeout || 30000;
    } else {
      this.timeout = this._customTimeout;
    }
  }

  /**
   * 设置 Token
   * @param {string} token - 访问令牌
   */
  setToken(token) {
    this.token = token;
  }

  /**
   * 设置基础 URL
   * @param {string} url - 新的基础 URL
   */
  setBaseUrl(url) {
    this._customBaseUrl = url;
    this.baseUrl = joinUrlSegments(url, '');
  }

  /**
   * 获取当前使用的 Token
   * 优先使用实例 Token，否则从存储获取
   * @returns {string|null}
   */
  getToken() {
    return this.token || getStoredToken();
  }

  /**
   * 发送 HTTP 请求
   * @param {string} endpoint - API 端点（相对于 baseUrl）
   * @param {Object} options - fetch 选项
   * @returns {Promise<any>} 响应数据
   */
  async request(endpoint, options = {}) {
    const url = /^https?:\/\//i.test(endpoint) ? endpoint : `${this.baseUrl}${endpoint}`;
    const retryCount = options._retryCount || 0;

    // 构建请求头
    const headers = {
      'Content-Type': 'application/json',
      'Accept-Language': this.getLanguage(),
      ...options.headers,
    };

    // 添加认证头
    const token = this.getToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    // 超时控制
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      // 5xx 服务端错误 — 可重试
      if (response.status >= 500 && retryCount < this.maxRetries) {
        const delay = this.retryDelay * Math.pow(2, retryCount);
        logger.warn(`${this.constructor.name} 5xx error, retrying in ${delay}ms... (${retryCount + 1}/${this.maxRetries})`);
        await new Promise(r => setTimeout(r, delay));
        return this.request(endpoint, { ...options, _retryCount: retryCount + 1 });
      }

      // 解析响应
      const data = await parseResponseBody(response);

      // 处理错误响应
      if (!response.ok) {
        let errorMsg =
          (data && typeof data === 'object' ? data.message || data.detail : null) ||
          (typeof data === 'string' ? data : null);
        if (Array.isArray(errorMsg)) {
          errorMsg = errorMsg[0]?.msg || errorMsg[0]?.message || JSON.stringify(errorMsg);
        } else if (typeof errorMsg === 'object' && errorMsg !== null) {
          errorMsg = errorMsg.msg || errorMsg.message || JSON.stringify(errorMsg);
        }

        const error = new Error(errorMsg || `HTTP ${response.status}`);
        error.code = data?.code || `HTTP_${response.status}`;
        error.status = response.status;
        error.data = data;

        this.onError?.(error);
        logger.error(`${this.constructor.name} Error:`, error.message);
        throw error;
      }

      return data;
    } catch (error) {
      clearTimeout(timeoutId);

      // 处理超时错误
      if (error.name === 'AbortError') {
        const timeoutError = new Error('Request timeout');
        timeoutError.code = 'TIMEOUT';
        this.onError?.(timeoutError);
        throw timeoutError;
      }

      // 处理网络错误 — 可重试
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        if (retryCount < this.maxRetries) {
          const delay = this.retryDelay * Math.pow(2, retryCount);
          logger.warn(`${this.constructor.name} Network error, retrying in ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          return this.request(endpoint, { ...options, _retryCount: retryCount + 1 });
        }
        const networkError = new Error('Network error');
        networkError.code = 'NETWORK_ERROR';
        this.onError?.(networkError);
        throw networkError;
      }

      throw error;
    }
  }

  /**
   * GET 请求便捷方法
   * @param {string} endpoint - API 端点
   * @param {Object} params - 查询参数
   */
  async get(endpoint, params = {}) {
    const query = new URLSearchParams(params).toString();
    const url = query ? `${endpoint}?${query}` : endpoint;
    return this.request(url, { method: 'GET' });
  }

  /**
   * POST 请求便捷方法
   * @param {string} endpoint - API 端点
   * @param {Object} data - 请求体数据
   */
  async post(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * PUT 请求便捷方法
   * @param {string} endpoint - API 端点
   * @param {Object} data - 请求体数据
   */
  async put(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  /**
   * DELETE 请求便捷方法
   * @param {string} endpoint - API 端点
   * @param {Object} data - 请求体数据（可选）
   */
  async delete(endpoint, data = null) {
    const options = { method: 'DELETE' };
    if (data) {
      options.body = JSON.stringify(data);
    }
    return this.request(endpoint, options);
  }

  /**
   * PATCH 请求便捷方法
   * @param {string} endpoint - API 端点
   * @param {Object} data - 请求体数据
   */
  async patch(endpoint, data = {}) {
    return this.request(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }

  /**
   * 销毁实例，取消配置订阅
   */
  destroy() {
    if (this._unsubscribe) {
      this._unsubscribe();
      this._unsubscribe = null;
    }
  }
}

/**
 * 创建 API 客户端的工厂函数
 * 用于快速创建简单的 API 客户端
 * 
 * @param {string} modulePath - 模块路径
 * @param {Object} methods - 方法定义对象
 * @returns {BaseApiClient} API 客户端实例
 * 
 * @example
 * const myApi = createApiClient('/my-module', {
 *   getList: (client) => client.get('/list'),
 *   create: (client, data) => client.post('/', data),
 * });
 */
export function createApiClient(modulePath, methods = {}) {
  const client = new BaseApiClient(modulePath);

  // 绑定自定义方法
  Object.entries(methods).forEach(([name, fn]) => {
    client[name] = (...args) => fn(client, ...args);
  });

  return client;
}

export default BaseApiClient;
